import firebase from "firebase/compat/app";
import { createAuthLink, AUTH_TYPE } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { RetryLink } from "@apollo/client/link/retry";
import { split, HttpLink } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { getVar } from "config";
import logger from "logging/logger";
import { Actions, Errors, trackAction, trackError } from "modules/monitoring";

const url = getVar("REACT_APP_APP_SYNC_ENDPOINT");
const region = getVar("REACT_APP_AWS_REGION");

const firebaseAuthPromise = new Promise<void>((resolve) => {
  firebase.auth().onAuthStateChanged((user) => {
    if (user !== null) {
      resolve();
    }
  });
});

export const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      logger.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) logger.error(`[Network error]: ${networkError}`);
});

export const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: (count, operation, error) => {
    // Total attempts will run 8 times over the course of 2.5 minutes.
    // Each subsequent retry will be twice the length of the last retry
    if (count > 9) {
      trackError(Errors.GRAPHQL_RETRY_MAX, {
        label: Errors.GRAPHQL_RETRY_MAX,
        context: {
          operation: operation.operationName,
        },
      });
      return false;
    }
    trackAction(Actions.GRAPHQL_RETRY, {
      operation: operation.operationName,
      count,
    });
    return !!error;
  },
});

const getFirebaseAuthToken = async () => {
  await firebaseAuthPromise;
  const user = await firebase.auth().currentUser;
  const token = await user?.getIdToken(true);
  return token ?? "";
};

export const appSyncAuthLink = createAuthLink({
  url,
  region,
  auth: {
    type: AUTH_TYPE.OPENID_CONNECT,
    jwtToken: getFirebaseAuthToken,
  },
});

const appSyncSubscriptionLink = createSubscriptionHandshakeLink({
  url,
  region,
  auth: {
    type: AUTH_TYPE.OPENID_CONNECT,
    jwtToken: getFirebaseAuthToken,
  },
});

const httpLink = new HttpLink({ uri: url });

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  appSyncSubscriptionLink,
  httpLink,
);

export const authLinks = [retryLink, errorLink, appSyncAuthLink, splitLink];
